import React, { useCallback, useMemo } from 'react';
import { Button, ButtonGroup } from 'reactstrap';
import { Edit, Trash } from 'react-feather';
import i18next from 'i18next';

import SimpleTableWithSorting, {
  generateColumn,
  getRTOriginal,
  TableCellProps,
} from 'app/components/common/SimpleTableWithSorting';
import type { ColumnDef } from '@tanstack/react-table';
import { toDateInputValue, displayTime } from 'utils/date-time';
import { usageTypeOptions } from './UsageTypeSelect';
import { IPromoCode, UsageType } from 'types/promo-code';
import { promoCodelabelsByType } from './getPromoCodeLabel';
import { Link } from 'react-router-dom';

interface PromoCodesTableProps {
  onCreate: () => void;
  onEdit: (promoCode: IPromoCode) => void;
  onDelete: (promoCode: IPromoCode) => void;
  getOrdersRoute?: (promoCode: IPromoCode) => string;
  promoCodes: IPromoCode[] | null;
}

export const PromoCodesTable: React.FC<PromoCodesTableProps> = ({
  onCreate,
  onEdit,
  onDelete,
  getOrdersRoute,
  promoCodes,
}) => {
  const renderCodeWithActionsCell = useCallback(
    (props: TableCellProps<IPromoCode>) => {
      const promoCode = getRTOriginal<IPromoCode>(props);
      return (
        <div>
          <div>{promoCode.code}</div>
          <ButtonGroup size="sm" className="mt-2">
            <Button color="success" onClick={() => onEdit(promoCode)}>
              <Edit size="14" />
            </Button>
            <Button color="danger" onClick={() => onDelete(promoCode)}>
              <Trash size="14" />
            </Button>
            {getOrdersRoute && (
              <Link className='m-2' to={getOrdersRoute(promoCode)}>
                {i18next.t('promo-codes-table.show-orders')}
              </Link>
            )}
          </ButtonGroup>
        </div>
      );
    },
    [onEdit, onDelete, getOrdersRoute]
  );

  const renderDiscountTypeCell = useCallback(
    (props: TableCellProps<IPromoCode>) => {
      const promoCode = getRTOriginal<IPromoCode>(props);
      return (
        <div>
          <div>{promoCodelabelsByType()[promoCode.discount_type]}</div>
          {!isFreeDeliveryCode(promoCode) && (
            <div className="mt-1">{i18next.t('promo-codes-table.value')}: {promoCode.discount}</div>
          )}
        </div>
      );
    },
    []
  );

  const renderUsageTypeCell = useCallback(
    (props: TableCellProps<IPromoCode>) => {
      const promoCode = getRTOriginal<IPromoCode>(props);
      const usageTypeText = i18next.t(
        usageTypeOptions.find(
          (opt) => opt.value === promoCode.usage_type
        )?.name as string
      );
      return <div className="text-center">{usageTypeText}</div>;
    },
    []
  );

  const renderValidityPeriodCell = useCallback(
    (props: TableCellProps<IPromoCode>) => {
      const promoCode = getRTOriginal<IPromoCode>(props);
      const activatedAt = new Date(promoCode.activated_at);
      const deactivatedAt = new Date(promoCode.deactivated_at);
      
      return (
        <div className="min-w-200">
          <div>
            {i18next.t('promo-codes-table.from')}: {toDateInputValue(activatedAt)} {displayTime(activatedAt)}
          </div>
          {!!deactivatedAt ? (
            <div className="mt-1">
              {i18next.t('promo-codes-table.to')}: {toDateInputValue(deactivatedAt)} {displayTime(deactivatedAt)}
            </div>
          ) : (
            <div>{i18next.t('promo-codes-table.no-expiry')}</div>
          )}
        </div>
      );
    },
    []
  );

  const renderUsageCountCell = useCallback(
    (props: TableCellProps<IPromoCode>) => {
      const promoCode = getRTOriginal<IPromoCode>(props);
      return (
        <div className="text-center">
          {promoCode.orders_count}
          {promoCode.usage_type === UsageType.Single && promoCode.redeemed
            ? ' ' + i18next.t('promo-codes-table.max-usage')
            : ''}
        </div>
      );
    },
    []
  );

  const renderMinCartAmountCell = useCallback(
    (props: TableCellProps<IPromoCode>) => {
      const promoCode = getRTOriginal<IPromoCode>(props);
      return <div>{promoCode.min_cart_amount || i18next.t('Unset')}</div>;
    },
    []
  );

  const columns: ColumnDef<unknown, unknown>[] = useMemo(
    () => [
      generateColumn('code', {
        customHeader: i18next.t('promo-codes-table.code'),
        customCellFormatter: renderCodeWithActionsCell,
      }),
      generateColumn('description', {
        customHeader: i18next.t('promo-codes-table.description'),
      }),
      generateColumn('discount_type', {
        customHeader: i18next.t('promo-codes-table.promo-type'),
        customCellFormatter: renderDiscountTypeCell,
      }),
      generateColumn('usage_type', {
        customHeader: i18next.t('promocode.usage-type-label'),
        customCellFormatter: renderUsageTypeCell,
      }),
      generateColumn('activated_at', {
        customHeader: i18next.t('promo-codes-table.validity-period'),
        customCellFormatter: renderValidityPeriodCell,
        filterType: 'none',
      }),
      generateColumn('orders_count', {
        customHeader: i18next.t('promo-codes-table.usage-count'),
        customCellFormatter: renderUsageCountCell,
        filterType: 'none',
      }),
      generateColumn('min_cart_amount', {
        customHeader: i18next.t('promo-codes-table.min-cart'),
        customCellFormatter: renderMinCartAmountCell,
      }),
    ],
    [
      renderCodeWithActionsCell,
      renderDiscountTypeCell,
      renderUsageTypeCell,
      renderValidityPeriodCell,
      renderUsageCountCell,
      renderMinCartAmountCell,
    ]
  );

  if (!promoCodes || !promoCodes.length) {
    return (
      <div className="text-center p-5">
        <p>{i18next.t('promo-codes-list.empty')}</p>
        <Button color="success" onClick={onCreate}>
          {i18next.t('promo-codes-list.add-first')}
        </Button>
      </div>
    );
  }

  return (
    <SimpleTableWithSorting
      columns={columns}
      data={promoCodes}
      hasFilters
      pageSize={25}
      className="table-striped"
    />
  );
};

function isFreeDeliveryCode(promoCode: IPromoCode) {
  return promoCode.discount_type === 'freeDelivery';
}

export default PromoCodesTable;
